import type { ISODateTime } from "@albertine/types";
import { Timestamp } from "firebase/firestore";
import { z } from "zod";
import type { FirebaseTimestamp } from "../common/types/FirebaseTimestamp";

export function timestampFromDate(date: Date): Timestamp {
    return Timestamp.fromDate(date);
}

export function firebaseTimestampFromDate(date: Date): FirebaseTimestamp {
    const ts = Timestamp.fromDate(date);
    return {
        nanoseconds: ts.nanoseconds,
        seconds: ts.seconds,
    };
}

/** Convert an API ISO datetime to the Firestore-shaped timestamp used in LMT models. */
export function isoToFirebaseTimestamp(iso: string): FirebaseTimestamp;
export function isoToFirebaseTimestamp(iso: null): null;
export function isoToFirebaseTimestamp(iso: undefined): undefined;
export function isoToFirebaseTimestamp(
    iso: string | null | undefined,
): FirebaseTimestamp | null | undefined;
export function isoToFirebaseTimestamp(
    iso: string | null | undefined,
): FirebaseTimestamp | null | undefined {
    if (iso == null) {
        return iso;
    }
    return firebaseTimestampFromDate(new Date(iso));
}

export function toTimestamp(firebaseTimestamp: FirebaseTimestamp): Timestamp {
    if (
        firebaseTimestamp.seconds !== undefined &&
        firebaseTimestamp.nanoseconds !== undefined
    )
        return new Timestamp(firebaseTimestamp.seconds, firebaseTimestamp.nanoseconds);
    if (
        firebaseTimestamp._seconds !== undefined &&
        firebaseTimestamp._nanoseconds !== undefined
    )
        return new Timestamp(
            firebaseTimestamp._seconds,
            firebaseTimestamp._nanoseconds,
        );
    throw Error(`Invalid timestamp ${JSON.stringify(firebaseTimestamp)}`);
}

export function fromTimestampToDate(timestampStruct: FirebaseTimestamp): Date {
    return toTimestamp(timestampStruct).toDate();
}

export function fromTimestampToIsoDateString(
    timestampStruct: FirebaseTimestamp,
): string {
    return toTimestamp(timestampStruct).toDate().toISOString();
}

export function fromTimestampToDateOptional(
    timestampStruct: FirebaseTimestamp | null | undefined,
): Date | undefined {
    if (timestampStruct) return fromTimestampToDate(timestampStruct);
    return undefined;
}

const TimestampParser = z.object({
    seconds: z.number(),
    nanoseconds: z.number(),
});

export const isFirebaseTimestamp = (
    possibleTimestamp: any,
): possibleTimestamp is FirebaseTimestamp =>
    TimestampParser.safeParse(possibleTimestamp).success;

export const isTimestamp = (possibleTimestamp: any): possibleTimestamp is Timestamp =>
    TimestampParser.safeParse(possibleTimestamp) &&
    possibleTimestamp.toDate !== undefined;

export function compareTo(a: FirebaseTimestamp, b: FirebaseTimestamp): number {
    return fromTimestampToDate(a).getTime() - fromTimestampToDate(b).getTime();
}

export function compareToIsoDateTime(a: ISODateTime, b: ISODateTime): number {
    return Date.parse(a) - Date.parse(b);
}
